import { useState } from "react";
import { toast } from "sonner";

import { useCreateIMROWriter } from "@/api/mutations";
import { useGetMyProfile } from "@/api/queries";
import useWriterStore from "@/features/songs/album/store/writerStore";
import { formatDateForAPI, sendEvent, sendProperty } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";

import type { IMROWriterFormData } from "./useImroWriterForm";

type UseWriterMutationOptions = {
  onSuccess?: (writerData?: any) => void;
  onError?: (error: Error) => void;
  onValidationError?: (messages: string[]) => void;
};

export function useWriterMutation(options: UseWriterMutationOptions = {}) {
  const { onSuccess, onError, onValidationError } = options;
  const [pending, setPending] = useState(false);

  const selectedAccount = useAppStore(s => s.selectedAccount);
  const { addSelectedWriter } = useWriterStore();
  const createIMROWriterMutation = useCreateIMROWriter();
  const profile = useGetMyProfile();

  const createIMROWriter = async (data: IMROWriterFormData) => {
    setPending(true);

    try {
      // Handle date of birth formatting - supports both Date objects and DD/MM/YYYY strings
      let formattedDateOfBirth = "";
      if (data.dateofbirth != null) {
        if (data.dateofbirth instanceof Date) {
          // If it's a Date object from the date picker, format it to DD/MM/YYYY then to ISO
          const day = String(data.dateofbirth.getDate()).padStart(2, "0");
          const month = String(data.dateofbirth.getMonth() + 1).padStart(2, "0");
          const year = data.dateofbirth.getFullYear();
          const dateString = `${day}/${month}/${year}`;
          formattedDateOfBirth = formatDateForAPI(dateString) ?? "";
        }
        else if (typeof data.dateofbirth === "string" && data.dateofbirth.trim() !== "") {
          // If it's a string in DD/MM/YYYY format, format to ISO
          formattedDateOfBirth = formatDateForAPI(data.dateofbirth.trim()) ?? "";
        }
      }

      // CREATE mutation - matches legacy WritersForm addWriter logic
      const createData = {
        artistaccountcode: selectedAccount,
        firstname: data.firstname.trim(),
        lastname: data.lastname.trim(),
        country: data.country.trim(),
        countryofbirth: data.countryofbirth.trim(),

        // societycisaccode: 128, // IMRO specific code
        dateofbirth: formattedDateOfBirth,
        company: data.company?.trim(),
        emailaddress: data.emailaddress?.trim(),
        phonenumber: data.phonenumber?.trim(),
        pseudonym: data.pseudonym?.trim() ?? "",
        address1: data.address1.trim(),
        address2: data.address2?.trim() ?? "",
        address3: data.address3?.trim() ?? "",
        address4: data.address4?.trim() ?? "",
        address5: data.address5?.trim() ?? "",
        taxarea: data.taxarea?.trim() ?? "",

      };

      const result = await createIMROWriterMutation.mutateAsync(createData);

      // Check if the response indicates validation failure
      if (result.failedvalidation) {
        const messages = result.messages || ["IMRO registration failed due to validation errors"];
        onValidationError?.(messages);
        toast.error("IMRO registration failed due to validation errors. Please check the form and try again.");
        throw new Error(messages.join(", "));
      }

      if (result.model) {
        // Create display name from firstname/lastname if writername is not available
        let displayName = result.model.writername;
        if (!displayName || displayName.trim() === "") {
          const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
          displayName = parts.join(" ");
        }

        const updatedWriter = {
          writercode: result.model.writercode,
          writername: displayName,
          caeipinumber: result.model.caeipinumber,
        };

        // Add the newly created writer to selected writers
        addSelectedWriter(updatedWriter);
        toast.success(`IMRO writer registered successfully: ${updatedWriter.writername}`);

        // Send HubSpot tracking events
        sendProperty({ property: "last_writer_added", value: "CURRENT_DATE", email: profile?.data?.email });
        sendEvent({ property: "pe14489857_writer_added" });

        onSuccess?.(result.model);
        return result;
      }
      else {
        throw new Error("Failed to create IMRO writer - no model returned");
      }
    }
    catch (error) {
      console.error("Error creating IMRO writer:", error);

      let errorMessage = "IMRO registration failed. Please try again.";
      let isValidationError = false;

      if (error instanceof Error && error.message) {
        // Check if the error message contains multiple validation messages (separated by ", ")
        if (error.message.includes(", ") && error.message.length > 50) {
          // Likely validation errors joined by processRes
          isValidationError = true;
          const messages = error.message.split(", ");
          onValidationError?.(messages);
          toast.error("IMRO registration failed due to validation errors. Please check the form and try again.");
        }
        else {
          // Single error message or general error
          errorMessage = error.message;

          // Check for specific error types
          if (errorMessage.includes("validation") || errorMessage.includes("required") || errorMessage.includes("invalid")) {
            isValidationError = true;
            onValidationError?.([errorMessage]);
            toast.error("IMRO registration failed due to validation errors. Please check the form and try again.");
          }
        }
      }

      if (!isValidationError) {
        // Show toast for general errors with more user-friendly messages
        let userFriendlyMessage = errorMessage;

        if (errorMessage.includes("server error")) {
          userFriendlyMessage = "Server error occurred. Please try again later.";
        }
        else if (errorMessage.includes("network") || errorMessage.includes("fetch")) {
          userFriendlyMessage = "Network error. Please check your connection and try again.";
        }
        else if (errorMessage === "Failed to create IMRO writer - no model returned") {
          userFriendlyMessage = "IMRO registration completed but no writer data was returned. Please contact support.";
        }

        toast.error(`IMRO registration failed: ${userFriendlyMessage}`);
        onError?.(new Error(userFriendlyMessage));
      }

      throw error;
    }
    finally {
      setPending(false);
    }
  };

  return {
    createIMROWriter,
    pending,
  };
}
